import TRANSPARENT_BG from '@/resources/images/transparent-bg.png';

const ALLOWED_URL_SCHEMES = new Set(['https:', 'http:']);
// Only allow base64-encoded raster image data URIs — excludes svg+xml (active content risk),
// data:text/html, data:application/*, etc.
const ALLOWED_DATA_URI = /^data:image\/(png|jpe?g|gif|webp);base64,/i;

/**
 * Validates and encodes a URL for use in href and media src bindings.
 * Only http: and https: schemes are permitted. data: URIs are accepted
 * exclusively for base64-encoded images. All other inputs (including
 * javascript:, data:text/html, etc.) fall back to the transparent PNG.
 */
export function sanitizeHardLinkUrl(input: string | string[] | null | undefined): string | undefined {
  if (!input || input === 'None') return input as undefined;

  if (Array.isArray(input)) {
    if (input.length === 0) return undefined;
    input = input[0];
  }

  const trimmed = input.trim();

  // Allow safe base64 image data URIs (used for inline image src bindings).
  if (trimmed.startsWith('data:')) {
    return ALLOWED_DATA_URI.test(trimmed) ? trimmed : TRANSPARENT_BG;
  }

  // Normalise bare www. URLs so the URL constructor can parse them.
  const urlString = trimmed.startsWith('www.') ? `https://${trimmed}` : trimmed;

  try {
    const parsed = new URL(urlString);
    if (!ALLOWED_URL_SCHEMES.has(parsed.protocol)) return TRANSPARENT_BG;
    // Use parsed.href — already WHATWG-normalised; avoids double-encoding pre-encoded URLs.
    return parsed.href;
  } catch {
    return TRANSPARENT_BG;
  }
}

/**
 * Converts a Google Maps URL into an embeddable maps URL.
 */
export function getLatLongFromTheGoogleMapsUrl(url: string | undefined): string | undefined {
  if (!url) return;
  let latLong = '0,0';
  if (url.indexOf('/place/') > -1) [, latLong] = url.split('/place/');
  return `https://maps.google.com/maps?q=${latLong}&z=14&output=embed`;
}
